import React from 'react';
import { motion } from 'framer-motion';
import { useI18n } from '../lib/i18n';
import { AwardIcon, ShieldCheckIcon } from 'lucide-react';
export function Author() {
  const {
    t
  } = useI18n();
  const sections = [{
    title: t('author.vision'),
    text: t('author.visionText')
  }, {
    title: t('author.research'),
    text: t('author.researchText')
  }, {
    title: t('author.approach'),
    text: t('author.approachText')
  }];
  return <section className="w-full bg-white py-32 px-6 md:px-12 border-t border-black/[0.06]" data-id="element-51" data-mp="17gnpfmsdrvn1">
      <div className="max-w-6xl mx-auto" data-id="element-52" data-mp="sjerubchhvmv">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.6
      }} className="font-display text-4xl md:text-5xl text-[#0A0A0F] mb-16 tracking-tight" data-id="element-53" data-mp="sid539m3vkpb">
          {t('author.title')}
        </motion.h2>

        {/* ─── Portrait + Bio ───────────────────────────────────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 items-start" data-id="element-54" data-mp="22z8jpxqwc9eu">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-50px'
        }} transition={{
          duration: 0.6
        }} className="md:col-span-1" data-id="element-55" data-mp="22x5q73qjby5d">
            <div className="relative aspect-[4/5] w-full max-w-xs overflow-hidden rounded-2xl bg-[#FAFAFA] border border-black/[0.06] shadow-sm" data-id="element-56" data-mp="2ho4rrusydmfg">
              <img src="https://cdn.magicpatterns.com/uploads/4KZbwUHeEdE6zBTCA8kSeY/WhatsApp_Image_2025-10-30_at_23.06.33.jpg" alt="Lionel Gustavo Raggio, desarrollador del marco CLVN–LRR™" className="w-full h-full object-cover object-top grayscale-[20%] contrast-[1.02]" data-id="element-57" data-mp="2vopc4veinqb2" />
              {/* Subtle gradient overlay for refinement */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent pointer-events-none" data-id="element-58" data-mp="1xb1uyp71eo9h" />
              {/* Corner credential mark */}
              <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-[10px] font-mono tracking-widest uppercase text-white/90" data-id="element-59" data-mp="2726bvug9wj6y">
                <span data-id="element-60" data-mp="2bz67tcjes5kc">LGR–327B–Ω917</span>
                <span className="px-2 py-0.5 rounded-full bg-white/20 backdrop-blur-sm border border-white/30" data-id="element-61" data-mp="2g2ugjyjs1776">
                  λ
                </span>
              </div>
            </div>
            <div className="mt-6" data-id="element-62" data-mp="lol6rrbnzoyj">
              <h3 className="text-xl text-[#0A0A0F] font-medium" data-id="element-63" data-mp="lpmqslanrlse">
                Lionel Gustavo Raggio
              </h3>
              <p className="text-sm text-[#4A4A55] mt-2 leading-relaxed" data-id="element-64" data-mp="qnwnj0c16ug4">
                {t('author.role')}
              </p>
            </div>
          </motion.div>

          <div className="md:col-span-2 space-y-10" data-id="element-65" data-mp="ed5fipoz5799">
            {sections.map((s, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} className="border-l-2 border-violet-electric/40 pl-6" data-id="element-66" data-mp="9dke88mc0mtd" data-mpi={i}>
                <h4 className="text-xs uppercase tracking-widest text-violet-deep mb-3 font-medium" data-id="element-67" data-mp="3cv2loey6zrt9">
                  {s.title}
                </h4>
                <p className="text-base text-[#2A2A35] leading-relaxed font-light" data-id="element-68" data-mp="38so2ftqm2q1g">
                  {s.text}
                </p>
              </motion.div>)}
          </div>
        </div>

        {/* ─── Recognitions ─────────────────────────────────────────────── */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-100px'
      }} transition={{
        duration: 0.7
      }} className="mt-32 pt-16 border-t border-black/[0.06]" data-id="element-69" data-mp="qncwpm7ovfjm">
          <div className="flex items-center gap-3 mb-3" data-id="element-70" data-mp="1a9iyvc82xsee">
            <AwardIcon className="w-4 h-4 text-violet-electric" aria-hidden="true" data-id="element-71" data-mp="1ec5u7n3ylvgc" />
            <span className="text-xs uppercase tracking-widest text-violet-deep font-medium" data-id="element-72" data-mp="3v09zoorc7l3l">
              {t('author.recognitions')}
            </span>
          </div>
          <h3 className="font-display text-3xl md:text-4xl text-[#0A0A0F] mb-6 tracking-tight max-w-2xl" data-id="element-73" data-mp="2uo1dinx2g8l">
            {t('author.recognitionsText')}
          </h3>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 mt-12" data-id="element-74" data-mp="3mj9cfl1di9gv">
            {/* Trophies photo — visual proof */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.97
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6
          }} className="lg:col-span-2 relative aspect-[4/5] overflow-hidden rounded-2xl border border-black/[0.06] bg-[#FAFAFA] shadow-sm" data-id="element-75" data-mp="59kzcmjzkfvs">
              <img src="https://cdn.magicpatterns.com/uploads/3TKYxZpUNYsXQiHGNNdoku/premios.png" alt="Trofeos: Global Recognition Award 2025 e Insight Success — Influential People Who Define 2026" className="w-full h-full object-cover" data-id="element-76" data-mp="2q1mul8asu3zf" />
              <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" data-id="element-77" data-mp="3esaqiv3k50r1" />
              <div className="absolute bottom-4 left-4 right-4 text-white text-xs font-mono tracking-wider uppercase opacity-90" data-id="element-78" data-mp="3jkam6bclh7ht">
                Physical certifications · 2025–2026
              </div>
            </motion.div>

            {/* Award details */}
            <div className="lg:col-span-3 grid grid-cols-1 gap-6" data-id="element-79" data-mp="2vtevjhjvkic5">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="grid grid-cols-1 md:grid-cols-5 gap-5 p-2 bg-[#FAFAFA] border border-black/[0.06] rounded-2xl overflow-hidden" data-id="element-80" data-mp="2vvradluc6ckz">
                <div className="md:col-span-2 relative aspect-[4/3] md:aspect-auto overflow-hidden rounded-xl bg-white border border-black/[0.04]" data-id="element-81" data-mp="20lw3uwdd6e0p">
                  <img src="https://cdn.magicpatterns.com/uploads/pnSRj8kNJTYVATxKrEQuKL/GRA_Lionel_Gustavo_Raggio_2025.png" alt="Certificado Global Recognition Award 2025 a Lionel Gustavo Raggio" className="w-full h-full object-contain p-2" data-id="element-82" data-mp="1qurp9xrlov6e" />
                </div>
                <div className="md:col-span-3 p-5 flex flex-col justify-center" data-id="element-83" data-mp="1cvs1esbwwkba">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-violet-electric mb-2" data-id="element-84" data-mp="17wp7o41du0im">
                    2025 · Winner
                  </div>
                  <h4 className="text-lg font-medium text-[#0A0A0F] mb-2 leading-snug" data-id="element-85" data-mp="329x605okgu90">
                    {t('author.awardGRA')}
                  </h4>
                  <p className="text-sm text-[#4A4A55] leading-relaxed" data-id="element-86" data-mp="2b5vujb6zdmqe">
                    {t('author.awardGRADesc')}
                  </p>
                </div>
              </motion.div>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="p-6 bg-[#0A0A0F] text-white rounded-2xl relative overflow-hidden" data-id="element-87" data-mp="22b7o3hpo5z00">
                {/* Subtle violet glow */}
                <div className="absolute -right-20 -top-20 w-64 h-64 bg-violet-electric/20 blur-3xl rounded-full pointer-events-none" data-id="element-88" data-mp="3dqwfcl9yhk6m" />
                <div className="relative" data-id="element-89" data-mp="3hrpzr4o6wgur">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-violet-300 mb-2" data-id="element-90" data-mp="165nx8brloyrj">
                    2026 · Recognition
                  </div>
                  <h4 className="text-lg font-medium text-white mb-2 leading-snug" data-id="element-91" data-mp="125xacj5732sa">
                    {t('author.awardInsight')}
                  </h4>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-92" data-mp="1b4906iy8lwjo">
                    {t('author.awardInsightDesc')}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-violet-200" data-id="element-93" data-mp="1pru0k53q9hnw">
                    <ShieldCheckIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-94" data-mp="1yud3ep7ijua5" />
                    Insight Success · Independent Press
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}